import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search, MapPin, BadgeCheck, Sparkles, Users, ArrowRight, Calendar, Crown, Globe, Star, Briefcase, GraduationCap, Building2, User,
} from "lucide-react";
import { api, resolveFileUrl } from "../api";
import { useAuth } from "../contexts";

const TYPE_META = {
  creative: { label: "Creative", icon: Sparkles },
  talent: { label: "Talent", icon: Briefcase },
  education: { label: "Education", icon: GraduationCap },
  employer: { label: "Employer", icon: Building2 },
  fan: { label: "Fan", icon: User },
};

const DEFAULT_FILTERS = {
  q: "",
  account_type: "",
  state: "",
  city: "",
  availability: "",
  min_rate: "",
  max_rate: "",
  verified_only: false,
  sort: "trending",
};

export default function CultureConnectPage({ defaultType = "" }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [facets, setFacets] = useState({ states: [], cities: [], skills: [], type_counts: {}, availability_options: [] });
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS, account_type: defaultType });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/culture-connect/facets").then((r) => setFacets(r.data));
  }, []);

  useEffect(() => {
    setBusy(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v === "" || v === false || v === null || v === undefined) return;
      params.set(k, v);
    });
    api.get(`/culture-connect/search?${params.toString()}`)
      .then((r) => setRows(r.data))
      .finally(() => setBusy(false));
  }, [filters]);

  const reset = () => setFilters({ ...DEFAULT_FILTERS, account_type: defaultType });
  const activeFilters = useMemo(
    () => Object.entries(filters).filter(([k, v]) => k !== "sort" && k !== "account_type" && v !== "" && v !== false).length,
    [filters]
  );

  const totalUsers = Object.values(facets.type_counts || {}).reduce((a, b) => a + b, 0);
  const heroTitle = defaultType
    ? `${TYPE_META[defaultType]?.label || "Culture"} Directory`
    : "Find. Connect. Book.";

  return (
    <div className="pb-32" data-testid="culture-connect-page">
      {/* HERO */}
      <section className="relative grain-bg border-b border-neutral-800 overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: "linear-gradient(180deg, rgba(5,5,5,0.45) 0%, rgba(5,5,5,0.95) 95%), url('https://images.pexels.com/photos/167092/pexels-photo-167092.jpeg')",
            backgroundSize: "cover", backgroundPosition: "center",
          }}
        />
        <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6 py-20">
          <div className="font-mono-label text-[11px] text-[var(--accent)] mb-4 rise">CULTURE CONNECT</div>
          <h1 className="font-display text-5xl sm:text-7xl md:text-8xl uppercase leading-[0.9] max-w-4xl rise rise-2">
            {heroTitle.split(" ").slice(0, -1).join(" ")}
            <br/><span className="text-[var(--accent)]">{heroTitle.split(" ").slice(-1)}</span>
          </h1>
          <p className="text-neutral-300 max-w-2xl mt-6 text-lg rise rise-3">
            The network for creatives, talent, educators and brands across the United States. Hire, book, collaborate — all in one place.
          </p>
          <div className="flex flex-wrap gap-3 mt-8 rise rise-4">
            {!user && (
              <>
                <Link to="/signup" className="btn-primary" data-testid="cc-signup-btn">
                  Join Culture Connect <ArrowRight size={14} />
                </Link>
                <Link to="/login" className="btn-ghost" data-testid="cc-signin">Sign In</Link>
              </>
            )}
            {user && (
              <>
                <Link to="/jobs" className="btn-ghost"><Briefcase size={14} /> Jobs Board</Link>
                <Link to="/dashboard" className="btn-ghost">My Dashboard</Link>
              </>
            )}
          </div>
          <div className="mt-10 flex flex-wrap gap-6 font-mono-label text-[11px] text-neutral-400 rise rise-4">
            <Stat n={totalUsers} label="MEMBERS" />
            <Stat n={facets.type_counts?.creative || 0} label="CREATIVES" />
            <Stat n={facets.type_counts?.talent || 0} label="TALENT" />
            <Stat n={facets.type_counts?.education || 0} label="EDUCATORS" />
            <Stat n={facets.type_counts?.employer || 0} label="EMPLOYERS" />
            <Stat n={facets.states?.length || 0} label="STATES" />
          </div>
        </div>
      </section>

      {/* TYPE PILLS — only when not filtered by route */}
      {!defaultType && (
        <section className="border-b border-neutral-800 bg-[var(--surface)]">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-5 overflow-x-auto">
            <div className="flex gap-2 min-w-max">
              <TypePill active={!filters.account_type} onClick={() => setFilters((f) => ({ ...f, account_type: "" }))} label="All" />
              {Object.keys(TYPE_META).filter(k=>k!=="fan").map((k) => (
                <TypePill key={k} active={filters.account_type === k} onClick={() => setFilters((f) => ({ ...f, account_type: k }))} label={TYPE_META[k].label} icon={TYPE_META[k].icon} testid={`cc-type-${k}`} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* DIRECTORY */}
      <section className="border-b border-neutral-800">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-[280px_1fr] gap-8">
          {/* FILTERS */}
          <aside className="space-y-6" data-testid="cc-filters">
            <div className="flex items-center justify-between">
              <div className="font-mono-label text-[10px] text-[var(--accent)]">FILTERS</div>
              {activeFilters > 0 && (
                <button onClick={reset} className="font-mono-label text-[10px] text-neutral-400 hover:text-white" data-testid="cc-reset">CLEAR · {activeFilters}</button>
              )}
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                className="input-brut pl-9"
                placeholder="Search name, skill, service…"
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                data-testid="cc-search"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="font-mono-label text-[10px] text-neutral-400 mb-2">STATE</div>
                <select className="input-brut" value={filters.state} onChange={(e) => setFilters((f) => ({ ...f, state: e.target.value }))} data-testid="cc-state">
                  <option value="">Any</option>
                  {facets.states.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <div className="font-mono-label text-[10px] text-neutral-400 mb-2">CITY</div>
                <select className="input-brut" value={filters.city} onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))} data-testid="cc-city">
                  <option value="">Any</option>
                  {facets.cities.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <div className="font-mono-label text-[10px] text-neutral-400 mb-2">AVAILABILITY</div>
              <select className="input-brut" value={filters.availability} onChange={(e) => setFilters((f) => ({ ...f, availability: e.target.value }))} data-testid="cc-availability">
                <option value="">Any</option>
                {(facets.availability_options || []).map((a) => <option key={a} value={a}>{a.replace("_", " ").toUpperCase()}</option>)}
              </select>
            </div>
            <div>
              <div className="font-mono-label text-[10px] text-neutral-400 mb-2">RATE RANGE ($/HR)</div>
              <div className="flex gap-2">
                <input type="number" placeholder="Min" className="input-brut" value={filters.min_rate} onChange={(e) => setFilters((f) => ({ ...f, min_rate: e.target.value }))} data-testid="cc-min-rate" />
                <input type="number" placeholder="Max" className="input-brut" value={filters.max_rate} onChange={(e) => setFilters((f) => ({ ...f, max_rate: e.target.value }))} data-testid="cc-max-rate" />
              </div>
            </div>
            <div>
              <div className="font-mono-label text-[10px] text-neutral-400 mb-2">SORT</div>
              <select className="input-brut" value={filters.sort} onChange={(e) => setFilters((f) => ({ ...f, sort: e.target.value }))} data-testid="cc-sort">
                <option value="trending">Trending</option>
                <option value="recent">Recently joined</option>
                <option value="rate_low">Rate: low → high</option>
                <option value="rate_high">Rate: high → low</option>
                <option value="alpha">A–Z</option>
              </select>
            </div>
            <label className="flex items-center gap-2 font-mono-label text-[11px] text-neutral-300 cursor-pointer">
              <input type="checkbox" checked={filters.verified_only} onChange={(e) => setFilters((f) => ({ ...f, verified_only: e.target.checked }))} data-testid="cc-verified" />
              VERIFIED ONLY
            </label>
            <div className="border border-neutral-800 p-4 mt-6">
              <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2 flex items-center gap-1"><Sparkles size={12} /> HOW IT WORKS</div>
              <ol className="space-y-2 font-mono-label text-[10px] text-neutral-400 leading-relaxed">
                <li>01 · Filter & find the right pro for your project.</li>
                <li>02 · Message them directly to scope the work.</li>
                <li>03 · Send a booking request with budget & date.</li>
                <li>04 · They accept → secured by our 15% platform fee + 85% creator split.</li>
              </ol>
            </div>
          </aside>

          {/* RESULTS */}
          <div>
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">DIRECTORY</div>
                <h2 className="font-headline text-2xl md:text-3xl uppercase" data-testid="cc-result-count">
                  {rows.length} {rows.length === 1 ? "Result" : "Results"}
                </h2>
              </div>
            </div>
            {busy ? (
              <div className="font-mono-label text-[10px] text-neutral-500">Loading roster…</div>
            ) : rows.length === 0 ? (
              <div className="border border-dashed border-neutral-800 p-12 text-center" data-testid="cc-empty">
                <Users size={28} className="mx-auto text-neutral-600 mb-4" />
                <p className="font-mono-label text-[11px] text-neutral-500">No one matches these filters yet.</p>
                <button onClick={reset} className="btn-ghost mt-6">Reset filters</button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cc-grid">
                {rows.map((u) => <PersonCard key={u.id} u={u} />)}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="border-b border-neutral-800 bg-[var(--surface)]">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-12 grid sm:grid-cols-3 gap-8">
          <Pillar icon={Globe} title="One platform" body="Discovery, messaging, bookings, payouts and reviews on a single profile." />
          <Pillar icon={Calendar} title="Real bookings" body="Send a date, scope and budget. Booked work is secured — 15% platform fee, 85% to the creator." />
          <Pillar icon={Crown} title="Verified culture" body="Verified pros, Culture Pass supporters, real reviews — credibility you can scan in a glance." />
        </div>
      </section>
    </div>
  );
}

function Stat({ n, label }) {
  return <span><span className="font-headline text-2xl text-white">{n}</span> {label}</span>;
}

function TypePill({ active, onClick, label, icon: Icon, testid }) {
  return (
    <button
      onClick={onClick}
      className={`font-mono-label text-[10px] px-4 py-2 border flex items-center gap-2 ${active ? "border-[var(--accent)] text-[var(--accent)]" : "border-neutral-800 text-neutral-400 hover:text-white"}`}
      data-testid={testid}
    >
      {Icon && <Icon size={12} />} {label.toUpperCase()}
    </button>
  );
}

function PersonCard({ u }) {
  const TypeIcon = TYPE_META[u.account_type]?.icon || User;
  return (
    <Link to={`/profile/${u.username}`} className="card-brut p-5 flex flex-col items-start gap-3" data-testid={`person-card-${u.username}`}>
      <div className="flex items-center gap-3 w-full">
        {u.avatar ? (
          <img src={resolveFileUrl(u.avatar)} alt={u.display_name} className="w-14 h-14 object-cover border border-neutral-800" />
        ) : (
          <div className="w-14 h-14 bg-neutral-900 border border-neutral-800 flex items-center justify-center"><TypeIcon size={20} /></div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-headline text-base uppercase truncate">{u.display_name}</div>
            {u.verified && <BadgeCheck size={14} className="text-[var(--accent)] shrink-0" />}
          </div>
          <div className="font-mono-label text-[10px] text-neutral-500 truncate">
            {u.service_title || u.company_name || `@${u.username}`}
          </div>
        </div>
      </div>
      {u.bio && <p className="text-neutral-400 text-sm line-clamp-2">{u.bio}</p>}
      <div className="flex flex-wrap gap-2 mt-1">
        {u.account_type && <span className="chip"><TypeIcon size={10} className="inline mr-1" />{TYPE_META[u.account_type]?.label?.toUpperCase()}</span>}
        {(u.city || u.state) && <span className="chip inline-flex items-center gap-1"><MapPin size={10} /> {[u.city, u.state].filter(Boolean).join(", ").toUpperCase()}</span>}
        {u.rate_amount > 0 && <span className="chip chip-accent">${u.rate_amount}/{u.rate_unit || "hr"}</span>}
      </div>
      <div className="flex items-center gap-3 mt-auto pt-3 w-full font-mono-label text-[10px] text-neutral-500">
        {u.rating_count > 0 ? (
          <span className="flex items-center gap-1"><Star size={11} className="text-[var(--accent)] fill-[var(--accent)]" /> {u.rating_avg.toFixed(1)} ({u.rating_count})</span>
        ) : <span className="text-neutral-600">No reviews yet</span>}
        {u.booking_count > 0 && <span>· {u.booking_count} bookings</span>}
      </div>
    </Link>
  );
}

function Pillar({ icon: Icon, title, body }) {
  return (
    <div>
      <Icon size={22} className="text-[var(--accent)] mb-3" />
      <div className="font-headline text-lg uppercase mb-2">{title}</div>
      <p className="text-neutral-400 text-sm">{body}</p>
    </div>
  );
}
